'use client';

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert } from './alert';
import { toast } from '@/lib/toast';
import { Loader2 } from 'lucide-react';
import { SPACING, TYPOGRAPHY } from '@/lib/design-tokens';
import { AUTHOR_EMAIL } from '@/lib/site-config';
import { useFormValidation, validators } from '@/hooks/use-form-validation';

export function ContactForm() {
  const searchParams = useSearchParams();
  const roleInitialized = useRef(false);
  const confirmationRef = useRef<HTMLHeadingElement>(null);
  // Server-supplied confirmation text; non-null once a submission succeeds
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { values, fieldStates, isSubmitting, setValue, handleBlur, handleSubmit, reset } =
    useFormValidation({
      initialValues: {
        name: '',
        email: '',
        message: '',
        role: '',
        website: '', // Honeypot field
      },
      validationRules: {
        name: [
          validators.required('Please enter your name'),
          validators.minLength(2, 'Name must be at least 2 characters'),
        ],
        email: [
          validators.required('Please enter your email'),
          validators.email('Please enter a valid email address'),
        ],
        message: [
          validators.required('Please enter a message'),
          validators.minLength(20, 'Message must be at least 20 characters'),
          validators.maxLength(1000, 'Message must be less than 1000 characters'),
        ],
      },
      onSubmit: async (formValues) => {
        setSubmitError(null);
        const data = {
          name: formValues.name,
          email: formValues.email,
          message: formValues.message,
          role: formValues.role || undefined, // Optional, exclude if empty
          website: formValues.website, // Honeypot
        };

        try {
          const response = await fetch('/api/contact', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });

          const result = await response.json();

          if (!response.ok) {
            // Handle rate limiting with specific message
            if (response.status === 429) {
              const retryAfter = result.retryAfter || 60;
              setSubmitError(`Too many requests. Please try again in ${retryAfter} seconds.`);
            } else {
              setSubmitError(result.error || 'Failed to send message.');
            }
            return;
          }

          // Success: replace the form with an in-place confirmation
          if (result.warning) {
            toast.warning(result.message || 'Message received, but email delivery is unavailable.');
          }
          reset();
          setConfirmation(result.message || 'Message sent successfully!');
        } catch (error) {
          // If API is completely unavailable (404/blocking), show alternative contact methods
          if (error instanceof TypeError && error.message.includes('fetch')) {
            setSubmitError('The contact form is temporarily unavailable.');
          } else {
            setSubmitError('Something went wrong.');
          }
          console.error('Contact form error:', error);
        }
      },
    });

  // Read role from URL parameter and set it on component mount (only once)
  useEffect(() => {
    if (roleInitialized.current) return;

    const roleParam = searchParams.get('role');
    if (roleParam) {
      // Validate role parameter against known values
      const validRoles = ['executive', 'developer', 'security', 'other'];
      const normalizedRole = roleParam.toLowerCase();

      if (validRoles.includes(normalizedRole)) {
        setValue('role', normalizedRole);
      } else {
        // If unknown role, set to "other"
        setValue('role', 'other');
      }

      roleInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Move focus to the confirmation so keyboard and screen-reader users land on it
  useEffect(() => {
    if (confirmation) confirmationRef.current?.focus();
  }, [confirmation]);

  if (confirmation) {
    return (
      <div role="status" className={SPACING.compact}>
        <h2 ref={confirmationRef} tabIndex={-1} className={TYPOGRAPHY.h3.standard}>
          Message sent
        </h2>
        <p className="text-muted-foreground">{confirmation}</p>
        <Button type="button" variant="outline" onClick={() => setConfirmation(null)}>
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <div>
      <form onSubmit={handleSubmit} className={SPACING.content}>
        {/* Honeypot field - hidden from real users, visible to bots */}
        <div className="hidden" aria-hidden="true">
          <Label htmlFor="website">Website (leave blank)</Label>
          <Input
            id="website"
            name="website"
            type="text"
            autoComplete="off"
            tabIndex={-1}
            placeholder="https://example.com"
            value={values.website}
            onChange={(e) => setValue('website', e.target.value)}
          />
        </div>

        {/* Name field with validation */}
        <div className={SPACING.compact}>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            name="name"
            type="text"
            inputMode="text"
            placeholder="Your name"
            autoComplete="name"
            required
            disabled={isSubmitting}
            value={values.name}
            onChange={(e) => setValue('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            error={fieldStates.name.error}
            success={fieldStates.name.showSuccess}
          />
        </div>

        {/* Email field with validation */}
        <div className={SPACING.compact}>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            placeholder="your.email@example.com"
            autoComplete="email"
            required
            disabled={isSubmitting}
            value={values.email}
            onChange={(e) => setValue('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            error={fieldStates.email.error}
            success={fieldStates.email.showSuccess}
          />
        </div>

        {/* Role field - hidden but captures URL parameter */}
        <input type="hidden" name="role" value={values.role} aria-hidden="true" />

        {/* Message field with validation */}
        <div className={SPACING.compact}>
          <Label htmlFor="message">Message</Label>
          <Textarea
            id="message"
            name="message"
            placeholder="Your message (minimum 20 characters)..."
            required
            disabled={isSubmitting}
            rows={6}
            className="resize-none"
            value={values.message}
            onChange={(e) => setValue('message', e.target.value)}
            onBlur={() => handleBlur('message')}
            error={fieldStates.message.error}
            success={fieldStates.message.showSuccess}
          />
        </div>

        <p className="text-muted-foreground text-xs">
          By submitting this form, you agree to our data handling practices described in our{' '}
          {/* Underlined at rest: inside muted text, obsidian's base cut
              gives a text-primary link only 2.92:1 against its neighbours
              (axe link-in-text-block needs 3:1 or a non-colour cue). */}
          <a href="/privacy" className="text-primary underline underline-offset-4">
            Privacy Policy
          </a>
          . We collect only the information you provide (name, email, message) to respond to your
          inquiry. Your data is not shared with third parties and is handled securely.
        </p>

        {submitError && (
          <Alert type="critical">
            {submitError} You can also email{' '}
            <a href={`mailto:${AUTHOR_EMAIL}`} className="underline underline-offset-4">
              {AUTHOR_EMAIL}
            </a>
            .
          </Alert>
        )}

        <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="animate-spin" />}
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>
      </form>
    </div>
  );
}
